'use client';
import { getVariantFromSimpleControls } from '@/utils/animation';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && 'value' in x) return x.value ?? fallback;
    return x;
}

function hasMeaningfulText(value) {
    const raw = valOf(value, '');
    if (raw == null) return false;
    const text = String(raw)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;|&#160;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .trim();
    return text.length > 0;
}

function featureHasContent(feature) {
    if (!feature || typeof feature !== 'object') return false;
    const icon = String(valOf(feature.Icon, '')).trim();
    return (
        icon.length > 0 ||
        hasMeaningfulText(feature.Title) ||
        hasMeaningfulText(feature.Description)
    );
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'slide',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'slide';
}

const SERVICE_EDGE_SPACING = {
    large: {
        top: 'pt-28 md:pt-40 lg:pt-56 xl:pt-60 2xl:pt-64',
        bottom: 'pb-28 md:pb-40 lg:pb-56 xl:pb-60 2xl:pb-64',
    },
    'large-no-top': {
        top: 'pt-10',
        bottom: 'pb-28 md:pb-40 lg:pb-56 xl:pb-60 2xl:pb-64',
    },
    'large-no-bottom': {
        top: 'pt-28 md:pt-40 lg:pt-56 xl:pt-60 2xl:pt-64',
        bottom: 'pb-10',
    },
    'large-medium-bottom': {
        top: 'pt-28 md:pt-40 lg:pt-56 xl:pt-60 2xl:pt-64',
        bottom: 'pb-24 lg:pb-36 xl:pb-40',
    },
    'large-medium-top': {
        top: 'pt-24 lg:pt-36 xl:pt-40',
        bottom: 'pb-28 md:pb-40 lg:pb-56 xl:pb-60 2xl:pb-64',
    },
    none: {
        top: 'pt-10',
        bottom: 'pb-10',
    },
    'no-top': {
        top: 'pt-10',
        bottom: 'pb-24 lg:pb-36',
    },
    'no-bottom': {
        top: 'pt-24 lg:pt-36',
        bottom: 'pb-10',
    },
    medium: {
        top: 'pt-24 lg:pt-36 xl:pt-40',
        bottom: 'pb-24 lg:pb-36 xl:pb-40',
    },
};

function getServiceRowSpacing(spacing, index, total) {
    const preset = SERVICE_EDGE_SPACING[spacing] || SERVICE_EDGE_SPACING.medium;
    const defaultPreset = SERVICE_EDGE_SPACING.medium;
    const top = index === 0 ? preset.top : defaultPreset.top;
    const bottom = index === total - 1 ? preset.bottom : defaultPreset.bottom;
    return `${top} ${bottom}`;
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

export default function Services(props) {
    const {
        Services,
        Animation = 'fade',
        AnimationGroup = 'content',
        Duration = 0.6,
        EditorPreview = false,
        VerticalSpacing = 'medium',
    } = props;

    const animationValue = valOf(Animation, 'fade');
    const animationGroupValue = valOf(AnimationGroup, 'content');
    const durationVal = Number(valOf(Duration, 0.6)) || 0.6;

    const preset = normalizeAnimation(animationValue);

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (e) => setPrefersReduced(e.matches);
        setPrefersReduced(mq.matches);
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const isStagger = preset === 'stagger';
    const viewport = { once: !EditorPreview, amount: 0.5 };

    const rawGroup = String(animationGroupValue || 'content').toLowerCase();
    const allowed = new Set(['features', 'content', 'ctas', 'medias']);
    const group = allowed.has(rawGroup) ? rawGroup : 'content';

    const animateMedia = group === 'medias';
    const animateTextBlocks = group === 'content';
    const animateFeatures = group === 'content' || group === 'features';
    const animateCTAs = group === 'content' || group === 'ctas';

    const baseKey = `services-${group}-${preset}-${durationVal}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const variants = getVariantFromSimpleControls(
                'stagger',
                durationVal
            );
            if (EditorPreview)
                return { variants, initial: 'hidden', animate: 'show' };
            return {
                variants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        return {};
    }, [prefersReduced, isStagger, durationVal, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (prefersReduced) return {};
        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, durationVal - 0.2) },
                },
            };
            if (EditorPreview)
                return {
                    variants: childVariants,
                    initial: 'hidden',
                    animate: 'show',
                };
            return {
                variants: childVariants,
                initial: 'hidden',
                whileInView: 'show',
                viewport,
            };
        }
        if (preset === 'none') return {};
        const v = getVariantFromSimpleControls(preset, durationVal);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        if (EditorPreview)
            return { variants: v, initial: initialState, animate: targetState };
        return {
            variants: v,
            initial: initialState,
            whileInView: targetState,
            viewport,
        };
    }, [prefersReduced, isStagger, preset, durationVal, EditorPreview]);

    const animationConfig = {
        animateMedia,
        animateTextBlocks,
        animateFeatures,
        animateCTAs,
        enableAnim: !prefersReduced && preset !== 'none',
        isStagger,
        preset,
        childMotionProps,
        containerMotionProps,
        prefersReduced,
        duration: durationVal,
    };
    const serviceCount = Array.isArray(Services) ? Services.length : 0;

    return (
        <div data-json="services">
            {Services.map((Service, index) => (
                <ServiceTemplate
                    key={index}
                    {...Service}
                    rowSpacingClass={getServiceRowSpacing(
                        VerticalSpacing,
                        index,
                        serviceCount
                    )}
                    imagePosition={index % 2 === 0 ? 'left' : 'right'}
                    animationConfig={{
                        ...animationConfig,
                        baseKey: `${baseKey}-${index}`,
                    }}
                />
            ))}
        </div>
    );
}

function ServiceTemplate(props) {
    const {
        Tagline,
        Anchor,
        Header,
        Body,
        Image,
        ImagePosition = '50',
        AltText,
        Features = [],
        ColorScheme = 'light',
        rowSpacingClass = 'py-10',
        imagePosition,
        animationConfig = {},
    } = props;

    const visibleFeatures = Array.isArray(Features)
        ? Features.map((feature, index) => ({ feature, index })).filter(
              ({ feature }) => featureHasContent(feature)
          )
        : [];

    const {
        backgroundColor,
        textColor,
        headerColor,
        thirdButton,
        secondaryButton,
        iconColor,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);
    const { x: imagePosX, y: imagePosY } = parseImagePosition(ImagePosition);
    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;

    const {
        animateMedia = false,
        animateTextBlocks = false,
        animateFeatures = false,
        animateCTAs = false,
        enableAnim = false,
        isStagger = false,
        preset = 'none',
        baseKey = 'services',
        childMotionProps = {},
        containerMotionProps = {},
        duration = 0.6,
    } = animationConfig;

    const { hasAny: hasCTAs, element: CTAs } = getCTA(props, {
        secondaryVariant: secondaryButton,
        outlineVariant: thirdButton,
        buttonAlign: 'justify-start',
        wrapSpacing: 'mt-10 lg:mt-16',
        className: 'w-full',
        buttonsClassName: 'flex w-full flex-wrap justify-start gap-4',
        enableAnim: enableAnim && animateCTAs && preset !== 'none',
        motionProps:
            enableAnim && animateCTAs && preset !== 'none'
                ? childMotionProps
                : {},
    });

    return (
        <div
            id={Anchor}
            className={`editable-component relative ${backgroundColor}`}
            style={backgroundStyle}
        >
            <div className="grid grid-cols-1 items-center lg:grid-cols-2">
                <MotionMaybe
                    enable={enableAnim && animateMedia && preset !== 'none'}
                    k={`${baseKey}-media`}
                    motionProps={childMotionProps}
                    className={`${
                        imagePosition === 'right'
                            ? 'lg:order-last'
                            : 'lg:order-first'
                    } order-first h-full w-full`}
                >
                    <img
                        className="m-0 h-full w-full object-cover"
                        src={Image}
                        alt={AltText}
                        width={1000}
                        height={800}
                        style={{ objectPosition: imageObjectPosition }}
                    />
                </MotionMaybe>
                <div
                    className={`relative z-30 mx-auto max-w-2xl flex-col px-6 md:max-w-3xl md:px-12 lg:flex lg:justify-center lg:px-16 ${rowSpacingClass}`}
                >
                    {Tagline && (
                        <MotionMaybe
                            enable={
                                enableAnim &&
                                animateTextBlocks &&
                                preset !== 'none'
                            }
                            k={`${baseKey}-tagline`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`max-w-2xl text-base font-text uppercase leading-7 tracking-wide ${taglineColor}`}
                                components={{
                                    p: ({ children, ...rest }) => (
                                        <p
                                            {...rest}
                                            className={`mb-0 max-w-2xl text-base font-text uppercase leading-7 tracking-wide ${taglineColor}`}
                                        >
                                            {children}
                                        </p>
                                    ),
                                }}
                            >
                                {Tagline}
                            </MarkDown>
                        </MotionMaybe>
                    )}
                    {Header && (
                        <MotionMaybe
                            enable={
                                enableAnim &&
                                animateTextBlocks &&
                                preset !== 'none'
                            }
                            k={`${baseKey}-header`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                variant="h2"
                                className={`text-left font-display tracking-tight ${headerColor} text-3xl lg:max-w-6xl lg:text-4xl xl:text-5xl`}
                            >
                                {Header}
                            </MarkDown>
                        </MotionMaybe>
                    )}
                    {Body && (
                        <MotionMaybe
                            enable={
                                enableAnim &&
                                animateTextBlocks &&
                                preset !== 'none'
                            }
                            k={`${baseKey}-body`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`mt-6 text-left text-base font-text leading-7 md:text-lg ${textColor}`}
                            >
                                {Body}
                            </MarkDown>
                        </MotionMaybe>
                    )}
                    {visibleFeatures.length > 0 && (
                        <MotionMaybe
                            enable={
                                enableAnim &&
                                animateFeatures &&
                                isStagger &&
                                preset !== 'none'
                            }
                            k={`${baseKey}-features-container`}
                            motionProps={containerMotionProps}
                            className={`mt-10 text-base leading-7 ${textColor}`}
                        >
                            <div className="space-y-5">
                                {visibleFeatures.map(({ feature, index }) =>
                                    isStagger &&
                                    enableAnim &&
                                    animateFeatures &&
                                    preset !== 'none' ? (
                                        <motion.div
                                            key={`feature-${index}-${baseKey}`}
                                            variants={{
                                                hidden: { opacity: 0, y: 16 },
                                                show: {
                                                    opacity: 1,
                                                    y: 0,
                                                    transition: {
                                                        duration: Math.max(
                                                            0.4,
                                                            duration - 0.2
                                                        ),
                                                    },
                                                },
                                            }}
                                        >
                                            <Feature
                                                {...feature}
                                                iconColor={iconColor}
                                                textColor={textColor}
                                            />
                                        </motion.div>
                                    ) : (
                                        <MotionMaybe
                                            key={`feature-${index}-${baseKey}`}
                                            enable={
                                                enableAnim &&
                                                animateFeatures &&
                                                !isStagger &&
                                                preset !== 'none'
                                            }
                                            k={`${baseKey}-feature-${index}`}
                                            motionProps={childMotionProps}
                                        >
                                            <Feature
                                                {...feature}
                                                iconColor={iconColor}
                                                textColor={textColor}
                                            />
                                        </MotionMaybe>
                                    )
                                )}
                            </div>
                        </MotionMaybe>
                    )}
                    {hasCTAs && CTAs}
                </div>
            </div>
        </div>
    );
}

function Feature({ Icon, Title, Description, iconColor, textColor }) {
    return (
        <div className="relative">
            <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-start justify-center">
                    <i
                        className={`${Icon} text-2xl leading-none ${iconColor}`}
                    />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="inline font-bold text-neutral-800">
                        <span className={`${textColor} font-display text-base`}>
                            <MarkDown
                                wrapperClassName="inline"
                                className={`${textColor} font-display text-base`}
                                components={{
                                    p: ({ children, ...rest }) => (
                                        <span
                                            {...rest}
                                            className={`${textColor} font-display text-base`}
                                        >
                                            {children}
                                        </span>
                                    ),
                                }}
                            >
                                {Title}
                            </MarkDown>
                        </span>
                    </div>
                    <div
                        className={`text-base font-text leading-5 ${textColor}`}
                    >
                        <MarkDown>{Description}</MarkDown>
                    </div>
                </div>
            </div>
        </div>
    );
}
